<template>
	<div>
		<div class="row mx-0">
			<label class="col-form-label mt-1 pr-1">Source:</label>
			<select v-model="internalValue.node" :disabled="true || disabled" class="col-3 form-control mt-1 mr-1 pr-0">
				<option value="">(Same Node)</option>
			</select>
			<select v-model="internalValue.project" :disabled="disabled" class="col form-control mt-1 mr-1 pr-0">
				<option v-if="isHomeNode && !internalValue.node && validation.isAllowedProject( '' )" value="">(Same Project)</option>
				<!--<option v-if="isHomeNode" :value="myProjectId">{{myProject.title}}</option> Include current project first -->
				<option :value="p.$id" v-for="p in projectList" :key="p.$id">{{p.title}} ({{p.$id}})</option>
			</select>
			<select v-model="internalValue.collection" :disabled="disabled" class="col form-control mt-1 pr-0">
				<option v-if="isHomeProject && !internalValue.project && validation.isAllowedCollection( '' )" value="">(Same Collection)</option>
				<!--<option v-if="isHomeProject" :value="myCollectionName">{{myCollection.title}}</option> Include current collection first -->
				<option :value="getCollectionName( c.$id )" v-for="c in collectionList" :key="c.$id">{{c.title}} ({{getCollectionName( c.$id )}})</option>
			</select>
		</div>
		<div class="row mx-0">
			<label class="col-form-label mt-1 pr-1">Object:</label>
			<input v-model="internalValue.ref" v-if="referenceStyle == 'manual'" :disabled="disabled" type="text" class="form-control mt-1 col" placeholder="[Enter Object ID]">
			<template v-else-if="referenceStyle == 'select'">
				<select v-model="internalValue.ref" :disabled="disabled" class="col form-control mt-1 pr-0">
					<option :value="undefined">- Select Object -</option>
					<option :value="o.$id" v-for="o in objectList" :key="o.$id">{{getObjectName( o )}}</option>
				</select>
				<router-link v-if="internalValue.ref" :to="getObjectUrlPath( { id : internalValue.ref } )" class="btn btn-secondary mt-1 ml-1"><i class="fas fa-external-link-alt"></i></router-link>
			</template>
			<label v-else-if="referenceStyle == 'search'" class="col-form-label mt-1 pl-1"><em class="text-danger">(Coming Soon)</em></label>
		</div>
	</div>
</template>

<script>
/*	REFERENCE FIELD AND VALIDATION SPEC:
{ $ref : "<id>", $source : "<node>/<project>:<collection>" }

Creation Options:

[SOURCE] [Node]/[Project]:[Collection]
[OBJECT] [Object][PREVIEW]

Node [-local-]	| [<my-node> (explicit)] | [<other-node>]
Project [-local-] | [<my-project> (explicit)] | [<other-project>]
Collection [-local-] | [<my-collection> (explicit)] | [<other-collection>]

Object [<title> (<$id>)] | [<name> (<$id>)] | [<$id>]

------------------

Validator Options:

[REGEX] ID Format
[ARRAY] Allowed IDs

[REGEX] Collection Name Format
[ARRAY] Allowed Collections
[BOOL] Disallow "Same Collection"

[ ] Allow Other Projects

	[REGEX] Project Name Format
	[ARRAY] Allowed Projects
	[BOOL] Disallow "Same Project"
	
	[ ] Allow Other Nodes
	
		[REGEX] Node Name Format
		[ARRAY] Allowed Nodes
		[BOOL] Disallow "Same Node"
*/
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

import _ from 'lodash';
import { randomString } from '../../util';
import Validation from "@/Validation.class";

export default {
	name: "object-editor-reference",
	//props: [ 'nodeId', 'projectId', 'collectionName', 'objectId' ],
	props: {
		'referenceStyle': { type: String, default: "select" },
		'validation': { type: Object, validator: function( value ) { return value instanceof Validation; }, required : false, default : () => (new Validation( { } )) },
		// we need to define prop value to hook into v-model (SET [v-model] ON COMPONENT INCLUSION AS NORMAL)
		'value': { type: Object, default: () => ({}) },	// Entire { $ref, $source } structure.
		'disabled': { type: Boolean }
    },
	data() {
		return {
			internalValue:
			{
				ref: undefined,
				node: undefined,
				project: undefined,
				collection: undefined
			},
			fetchedCollections: { },
			fetchedObjects : { },
			fetchedProjects : { },
		};
	},
	watch:
	{
		"internalValue.collection": function( newVal, oldVal )
		{
			console.warn( `COLLECTION: ${oldVal} --> ${newVal}` );
			if( oldVal !== undefined )
			{
				console.warn( `RESETTING OBJECT REF` );
				this.$set( this.internalValue, 'ref', undefined );
			}
		},
		"internalValue.project": function( newVal, oldVal )
		{
			console.warn( `PROJECT: ${oldVal} (${typeof oldVal}) --> ${newVal} (${typeof newVal})` );

			let resetCollection = ( oldVal !== undefined );	// Only consider resetting the collection, if it's not the first time it's being set.
			
			// Special case: if we're switching from this project explicitly stated, to an empty value (implicit THIS project) then don't reset the collection.
			if( this.myProjectId )
			{
				if( ( oldVal == this.myProjectId && newVal === '' ) || ( oldVal === '' && newVal == this.myProjectId ) )
					resetCollection = false;
			}

			// Only skip the collection reset, if [also] there is a current collection (empty string for THIS collection, or an explicitly stated one).
			if( !resetCollection && typeof this.internalValue.collection === 'string' )
				return console.warn( "NOT RESETTING COLLECTION!" );

			let collection;
			if( !newVal || this.myProjectId == newVal || this.isHomeProject )
				collection = this.myCollectionName;
			else if( this.collectionList.length > 0 )
				collection = this.getCollectionName( this.collectionList[0].$id );
			else
				collection = false;	// Special value for "No collections available..."
			
			this.$set( this.internalValue, 'collection', collection );
		},
		internalValue:
		{
			deep : true,
			handler( newVal, oldVal )
			{
				const realValue = this.toRealValue( newVal );
				// console.log( JSON.stringify( "NEW REAL VALUE:", realValue ) );

				// // we need to emit input to hook into v-model
				if( !_.isEqual( realValue, this.value ) )
				{
					this.$emit( 'input', realValue );
					this.$emit( 'onChanged', realValue );
				}
			}
		},
		//Listen back to the original value from the outside!
		value: function ( newVal, oldVal )
		{
			if( !_.isEqual( newVal, this.toRealValue( this.internalValue ) ) )
				this.updateInternalValue( newVal );
		}
	},
	computed:
	{
		// ...mapGetters( "objects", {
		// 	object : "object",
		// 	objects : "objects",
		// }),
		...mapGetters( "collections", {
			collection : "collection",
			collections : "collections",
		}),
		...mapGetters( "projects", {
			project : "project",
			projects : "projects",
		}),
		// ...mapGetters( "nodes", {
		// 	node,
		// 	homeNode,
		// 	rootNode,
		// 	parent
		// }),

		currentCollectionName( ) { return this.internalValue.collection || ( this.internalValue.collection === false ? undefined : this.myCollectionName ); },
		currentProjectId( ) { return this.internalValue.project || ( this.internalValue.project === false ? undefined : this.myProjectId ); },
		currentNodeId( ) { return this.internalValue.node || ( this.internalValue.node === false ? undefined : this.myNodeId ); },

		isHomeNode( ) { return this.myNodeId === this.currentNodeId },
		isHomeProject( ) { return this.myNodeId === this.currentNodeId && this.myProjectId === this.currentProjectId; },
		isHomeCollection( ) { return this.myNodeId === this.currentNodeId && this.myProjectId === this.currentProjectId && this.myCollectionName === this.currentCollectionName },

		objectList( )
		{
			if( !this.currentNodeId || !this.currentProjectId || !this.currentCollectionName )
				return [];
			this.getObjectList( );	// Initiate a load...
			let list;
			try {
				list = this.fetchedObjects[this.currentNodeId][this.currentProjectId][this.currentCollectionName].filter( v => this.validation.isAllowedObject( v.$id ) );
			} catch( err ) { list = []; }
			return list;
		},
		otherCollectionsList( )
		{
			return this.collectionList.filter( c => this.myCollection.$id != c.$id );
		},
		collectionList( )
		{
			if( !this.currentNodeId || !this.currentProjectId )
				return [];
			const list = this.collections( this.currentNodeId, this.currentProjectId ).filter( v => this.validation.isAllowedCollection( this.getCollectionName( v.$id ) ) );
			this.getCollectionList( list );
			return list;
		},
		otherProjectsList( )
		{
			return this.projectList.filter( p => this.myProject.$id != p.$id );
		},
		projectList( )
		{
			if( !this.currentNodeId )
				return [];
			const list = this.projects( this.currentNodeId ).filter( v => this.validation.isAllowedProject( v.$id ) );
			this.getProjectList( list );
			return list;
		},
		myCollection( )
		{
			return this.collection( this.myNodeId, this.myProjectId, this.myCollectionName );
		},
		myProject( )
		{
			return this.project( this.myNodeId, this.myProjectId );
		},
		myCollectionName( )
		{
			try {
				return this.$router.currentRoute.params.collectionName;
			} catch( err ) {
				console.warn( "Cannot get collection name from the router params!" )
				console.error( err );
				return;
			}
		},
		
		myProjectId( )
		{
			try {
				return this.$router.currentRoute.params.projectId;
			} catch( err ) {
				console.warn( "Cannot get project ID from the router params!" )
				console.error( err );
				return;
			}
		},
		myNodeId( )
		{
			try {
				return this.$router.currentRoute.params.nodeId;
			} catch( err ) {
				console.warn( "Cannot get node ID from the router params!" )
				console.error( err );
				return;
			}
		}
	},
	methods:
	{
		// setInternalNode( newNode )
		// {	// Assert: this.internalValue is never not an object.
		// 	this.internalValue.node = ( newNode ? newNode : undefined );
		// },
		// setInternalProject( newProject )
		// {	// Assert: this.internalValue is never not an object.
		// 	const oldProject = this.internalValue.project || '';
		// 	newProject = newProject || '';

		// 	if( oldProject == newProject )
		// 	{
		// 		this.internalValue.project = '';

		// 	}


		// },
		// setInternalCollection( )
		// {

		// },
		// setInternalObjectRef( )
		// {

		// },
		getObjectUrlPath( { id = '', collection = '', project = '', node = '' } )
		{
			collection = collection || this.internalValue.collection || this.myCollectionName;
			project = project || this.internalValue.project || this.myProjectId;
			node = node || this.internalValue.node || this.myNodeId;
			return `/nodes/${node}/projects/${project}/collections/${collection}/objects/${id}`;
		},
		async getObjectList( )
		{
			try {
				const list = this.fetchedObjects[this.currentNodeId][this.currentProjectId][this.currentCollectionName];
				if( Array.isArray( list ) )
					return list;
			} catch( err ) { }

			const objects = await this.listObjects( {
				nodeId : this.currentNodeId,
				projectId : this.currentProjectId,
				collectionName : this.currentCollectionName,
				nocache : true,
				options : { pagesize: 100 }	// Max 100 results for unfiltered object selection.
			} );

			if( !this.fetchedObjects[this.currentNodeId] ) this.$set( this.fetchedObjects, this.currentNodeId, { } );
			if( !this.fetchedObjects[this.currentNodeId][this.currentProjectId] ) this.$set( this.fetchedObjects[this.currentNodeId], this.currentProjectId, { } );
			this.$set( this.fetchedObjects[this.currentNodeId][this.currentProjectId], this.currentCollectionName, objects );
			return objects;
		},
		async getCollectionList( )
		{
			try {
				if( this.fetchedCollections[this.currentNodeId][this.currentProjectId] === true )
					return this.collections( this.currentNodeId, this.currentProjectId );
			} catch( err ) { }
			
			if( !this.fetchedCollections[this.currentNodeId] ) this.fetchedCollections[this.currentNodeId] = { };
			this.fetchedCollections[this.currentNodeId][this.currentProjectId] = true;
			const results = await this.listCollections( { nodeId : this.currentNodeId, projectId : this.currentProjectId, nocache : true } );
			
			if( this.internalValue.collection === false && results.length > 0 )
				this.internalValue.collection = this.getCollectionName( results[0].$id );

			return results;
		},
		async getProjectList( )
		{
			try {
				if( this.fetchedProjects[this.currentNodeId] === true )
					return this.projects( this.currentNodeId );
			} catch( err ) { }

			this.fetchedProjects[this.currentNodeId] = true;
			const results = await this.listProjects( { nodeId : this.currentNodeId, nocache : true } );
			
			if( this.internalValue.project === false && results.length > 0 )
				this.internalValue.project = results[0].$id;

			return results;
		},
		getObjectName( object )
		{
			const name = ""+ ( object.title || object.name || "" );
			if( name )
				return name +` (${object.$id})`;
			return object.$id;
		},
		toRealValue( internalValue )
		{
			if( typeof internalValue.ref === 'undefined' )
				return undefined;
			const source = ( internalValue.node ? internalValue.node +'/' : '' ) + ( internalValue.project ? internalValue.project +':' : '' ) + ( internalValue.collection || '' );
			return { $ref : internalValue.ref, $source : source };
		},
		updateInternalValue( value )
		{
			const sourceParts = ( this.value.$source || "" ).split( /[\/:]/g ).reverse( );
			const newRef = {
				ref : value.$ref,
				node : ( sourceParts.length > 2 ? sourceParts[2] : "" ),
				project : ( sourceParts.length > 1 ? sourceParts[1] : "" ),
				collection : ( sourceParts.length > 0 ? sourceParts[0] : "" )
			};

			if( !this.internalValue || typeof this.internalValue !== 'object' )
			{
				console.warn( "% SETTING ENTIRE internalValue" );
				this.internalValue = newRef;
			}
			else
			{
				console.warn( "% SETTING SINGLE internalValue" );
				
				if( this.internalValue.node !== newRef.node )
					this.$set( this.internalValue, 'node', newRef.node );
				
				if( this.internalValue.project !== newRef.project )
					this.$set( this.internalValue, 'project', newRef.project );
				
				if( this.internalValue.collection !== newRef.collection )
					this.$set( this.internalValue, 'collection', newRef.collection );
				
				if( this.internalValue.ref !== newRef.ref )
					this.$set( this.internalValue, 'ref', newRef.ref );
			}
		},
		getCollectionName( idOrName )
		{
			if( !idOrName )
				return "";
			const parts = idOrName.split(':');
			if( parts.length > 1 )
				return parts[1];
			return parts[0];
		},
		...mapActions({
			listCollections: "collections/list",
			listProjects: "projects/list",
			listObjects: "objects/list",
			listSchemas: "schemas/list",
			readSchema: "schemas/read",
			readObject: "objects/read",
			updateObject: "objects/update",
			deleteObject: "objects/delete",
			deleteAug: "augmentations/delete",
			createAug: "augmentations/create",
			replaceAug: "augmentations/replace",
			readAug: "augmentations/read",
		}),
	},
	async created( )
	{
		this.updateInternalValue( this.value );

	},
	async mounted( )
	{
	}
};
</script>
<style scoped>

</style>


// WEBPACK FOOTER //
// src/components/utilities/ObjectEditorReference.vue